import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { PlusIcon, EyeIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import { ticketsApi, type TicketWithRelations } from '../../lib/api'
import { TicketForm } from '../../components/forms/TicketForm'
import { TicketStatusBadge } from '../../components/ui/TicketStatusBadge'
import { TicketPriorityBadge } from '../../components/ui/TicketPriorityBadge'
import { TicketCategoryBadge } from '../../components/ui/TicketCategoryBadge'
import { Pagination } from '../../components/ui/Pagination'
import { useTranslation } from '../../hooks/useTranslation'
import type { TicketStatus, TicketPriority, TicketCategory, TicketInput } from '../../../shared/types'

const STATUSES = ['open', 'in_progress', 'pending', 'resolved', 'closed'] as const
const CATEGORIES = ['support', 'internal', 'development'] as const
type SortField = 'ticketNumber' | 'subject' | 'status' | 'priority' | 'assignedTo' | 'createdAt'
type SortDirection = 'asc' | 'desc'

const STATUS_ORDER: Record<TicketStatus, number> = {
  open: 0,
  in_progress: 1,
  pending: 2,
  resolved: 3,
  closed: 4,
}

const PRIORITY_ORDER: Record<TicketPriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
}

export function TicketsListPage() {
  const [showForm, setShowForm] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState<TicketCategory | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('all')
  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  // Build filters object for API
  const filters: Record<string, string> = {}
  if (categoryFilter !== 'all') filters.category = categoryFilter
  if (statusFilter !== 'all') filters.status = statusFilter

  const { data: response, isLoading } = useQuery({
    queryKey: ['tickets', { page, limit, filters }],
    queryFn: () => ticketsApi.list({ page, limit, filters: Object.keys(filters).length > 0 ? filters : undefined }),
  })

  const { data: stats } = useQuery({
    queryKey: ['tickets', 'stats', categoryFilter],
    queryFn: () => ticketsApi.getStats(categoryFilter !== 'all' ? categoryFilter : undefined),
  })

  const tickets = response?.data

  // Handle filter changes - reset to page 1
  const handleCategoryFilter = (category: TicketCategory | 'all') => {
    setCategoryFilter(category)
    setPage(1)
  }

  const handleStatusFilter = (status: TicketStatus | 'all') => {
    setStatusFilter(status)
    setPage(1)
  }

  const createMutation = useMutation({
    mutationFn: ticketsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      setShowForm(false)
    },
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection(field === 'createdAt' ? 'desc' : 'asc')
    }
  }

  // Client-side sorting
  const sortedTickets = useMemo(() => {
    if (!tickets) return []

    const result = [...tickets]

    result.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'ticketNumber':
          comparison = a.ticketNumber.localeCompare(b.ticketNumber)
          break
        case 'subject':
          comparison = a.subject.localeCompare(b.subject)
          break
        case 'status':
          comparison = STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
          break
        case 'priority':
          comparison = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
          break
        case 'assignedTo':
          const nameA = a.assignedTo ? `${a.assignedTo.firstName} ${a.assignedTo.lastName}`.toLowerCase() : 'zzz'
          const nameB = b.assignedTo ? `${b.assignedTo.firstName} ${b.assignedTo.lastName}`.toLowerCase() : 'zzz'
          comparison = nameA.localeCompare(nameB)
          break
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [tickets, sortField, sortDirection])

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field
    return (
      <th
        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col -space-y-1">
            <ChevronUpIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
            <ChevronDownIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
          </div>
        </div>
      </th>
    )
  }

  if (isLoading) return <div>{t('common.loading')}</div>

  return (
    <>
      {/* Secondary column - Actions and filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.tickets.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('pages.tickets.description' as any)}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            {showForm ? t('common.cancel') : t('pages.tickets.addButton')}
          </button>

          {showForm && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-4">{t('pages.tickets.newTitle')}</h3>
              <TicketForm
                onSubmit={(data) => createMutation.mutate(data)}
                onCancel={() => setShowForm(false)}
                loading={createMutation.isPending}
                fixedCategory={categoryFilter !== 'all' ? categoryFilter : undefined}
              />
            </div>
          )}

          {/* Category Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('fields.category')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleCategoryFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  categoryFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => handleCategoryFilter(cat)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    categoryFilter === cat
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(`pages.tickets.categories.${cat}` as any)}
                </button>
              ))}
            </div>
          </div>

          {/* Status Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('fields.status')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleStatusFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {STATUSES.map(status => (
                <button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    statusFilter === status
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(`pages.tickets.statuses.${status}` as any)}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.tickets.stats.title' as any)}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.tickets.statuses.open' as any)}</dt>
                <dd className="font-medium text-gray-900">{stats?.open || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.tickets.statuses.in_progress' as any)}</dt>
                <dd className="font-medium text-gray-900">{stats?.in_progress || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.tickets.stats.unassigned' as any)}</dt>
                <dd className="font-medium text-gray-900">{stats?.unassigned || 0}</dd>
              </div>
              <div className="flex justify-between text-sm border-t pt-2">
                <dt className="text-gray-500">{t('pages.tickets.stats.total')}</dt>
                <dd className="font-medium text-gray-900">{response?.total || 0}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('pages.tickets.title')}</h1>
            <button
              type="button"
              onClick={() => setShowForm(!showForm)}
              className="mt-4 w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
            >
              <PlusIcon className="h-5 w-5" />
              {showForm ? t('common.cancel') : t('pages.tickets.addButton')}
            </button>
            {showForm && (
              <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <h2 className="text-lg font-medium mb-4">{t('pages.tickets.newTitle')}</h2>
                <TicketForm
                  onSubmit={(data) => createMutation.mutate(data)}
                  onCancel={() => setShowForm(false)}
                  loading={createMutation.isPending}
                />
              </div>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <SortableHeader field="ticketNumber">#</SortableHeader>
                  <SortableHeader field="subject">{t('fields.subject')}</SortableHeader>
                  <SortableHeader field="status">{t('fields.status')}</SortableHeader>
                  <SortableHeader field="priority">{t('fields.priority')}</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    {categoryFilter === 'support' ? t('fields.contact') : t('fields.type')}
                  </th>
                  <SortableHeader field="assignedTo">{t('fields.assignedTo')}</SortableHeader>
                  <SortableHeader field="createdAt">{t('fields.createdAt')}</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedTickets.map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                      <Link to={`/tickets/${ticket.id}`} className="hover:text-brand-600">
                        {ticket.ticketNumber}
                      </Link>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900">
                      <div className="max-w-xs truncate">
                        <Link to={`/tickets/${ticket.id}`} className="hover:text-brand-600">
                          {ticket.subject}
                        </Link>
                      </div>
                      {categoryFilter === 'all' && (
                        <div className="mt-1">
                          <TicketCategoryBadge category={ticket.category} />
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <TicketStatusBadge status={ticket.status} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <TicketPriorityBadge priority={ticket.priority} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {categoryFilter === 'support' ? (
                        ticket.contact ? (
                          <Link to={`/contacts/${ticket.contactId}`} className="hover:text-brand-600">
                            {ticket.contact.firstName} {ticket.contact.lastName}
                          </Link>
                        ) : (
                          '-'
                        )
                      ) : (
                        ticket.type ? t(`pages.tickets.types.${ticket.type}` as any) : '-'
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {ticket.assignedTo ? (
                        `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}`
                      ) : (
                        <span className="text-gray-400">{t('pages.tickets.unassigned' as any)}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <Link
                        to={`/tickets/${ticket.id}`}
                        className="text-brand-600 hover:text-brand-900"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                    </td>
                  </tr>
                ))}
                {sortedTickets.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-sm text-gray-500">
                      {t('common.noResults')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {response && response.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                page={page}
                totalPages={response.totalPages}
                total={response.total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit)
                  setPage(1)
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
